import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../Features/auth/authSlice';
import {
  useDeleteDiscountsMutation,
  useEditUserDiscountMutation,
  useGetDiscountByIdQuery
} from '../../Features/discountsSlice';
import { useTheme } from '../../ThemeContext';

import {
  ArrowLeftIcon,
  TagIcon,
  TrashIcon,
  SaveIcon,
  XIcon
} from '@heroicons/react/outline';

function EditDiscountView() {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { discount_id } = useParams();
  const { isOnline } = useIsOnline();

  const [progress, setProgress] = useState(0);

  const user = useSelector(selectCurrentUser);

  const [editDiscount] = useEditUserDiscountMutation();
  const [deleteDiscount] = useDeleteDiscountsMutation();

  const [input, setInput] = useState({
    name: '',
    value: '',
    is_percentage: '',
    status: '',
    user_id: user.id,
  });

  const [errors, setErrors] = useState({
    name: '',
    value: '',
    is_percentage: '',
  });

  const {
    data: discount,
    isLoading,
    isSuccess,
  } = useGetDiscountByIdQuery(discount_id);

  useEffect(() => {
    setProgress(30);

    if (isLoading) {
      setProgress(80);
    }

    if (isSuccess && discount?.data) {
      setInput(discount.data);
      setProgress(100);
    }

    if (!isLoading && !isSuccess) {
      setProgress(100);
    }

  }, [discount, isLoading, isSuccess]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setInput({
      ...input,
      [name]: value
    });

    if (errors[name] !== undefined) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handDelete = async (e, id) => {
    if (!isOnline) {
      return swal(
        "No Internet connection",
        "Please check Internet connection and try again"
      );
    }

    if (window.confirm("Are you sure you want to delete this discount?")) {
      try {
        setProgress(80);

        const resp = await deleteDiscount(id).unwrap();

        if (resp.hasOwnProperty('success') === true) {
          setProgress(100);
          return navigate(-1);
        }

        setProgress(100);

      } catch (err) {
        setProgress(100);

        swal(
          "Error",
          err.message || "Something went wrong",
          "error"
        );
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isOnline) {
      return swal(
        "No Internet connection",
        "Please check Internet connection and try again"
      );
    }

    try {
      setProgress(80);

      const resp = await editDiscount(input).unwrap();

      if (resp.success === true) {
        setProgress(100);
        return navigate(-1);
      }

      setProgress(100);

    } catch (err) {
      setProgress(100);

      if (err?.data?.hasOwnProperty('errors') === true) {
        setErrors({
          ...errors,
          name: err.data.errors.name || '',
          value: err.data.errors.value || '',
          is_percentage: err.data.errors.is_percentage || ''
        });
      } else if (err?.data?.hasOwnProperty('error') === true) {
        setInput({
          ...input,
          errorMassage: err.data.error
        });
      }
    }
  };

  const handleCheckBox = (e) => {
    const { name, checked } = e.target;

    setInput({
      ...input,
      [name]: checked ? 1 : 0
    });
  };

  const back = () => {
    if (!isOnline) {
      return swal(
        "No Internet connection",
        "Please check Internet connection and try again"
      );
    }

    setProgress(100);
    navigate(-1);
  };

  return (
    <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

      <LoadingBar
        color="#80b643"
        height="4px"
        progress={progress}
        onLoaderFinished={() => setProgress(0)}
      />

      <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">

        {/* PAGE HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">

          <div className="flex items-center gap-3">

            <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
              <TagIcon className="w-6 h-6 text-[#80b643]" />
            </div>

            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white">
                Edit Discount
              </h1>

              <p className="text-sm text-gray-500 dark:text-gray-400">
                Update discount details and availability
              </p>
            </div>

          </div>

          {/* BACK BUTTON */}
          <button
            type="button"
            onClick={back}
            className="inline-flex items-center justify-center gap-2
                       px-4 py-2.5 rounded-xl
                       border border-gray-200 dark:border-gray-700
                       bg-white dark:bg-gray-900
                       text-gray-600 dark:text-gray-300
                       hover:text-[#80b643]
                       hover:border-[#80b643]
                       transition-all duration-200
                       w-full sm:w-auto"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span>Back</span>
          </button>

        </div>

        {/* FORM CARD */}
        <div className="bg-white dark:bg-gray-900
                        border border-gray-200 dark:border-gray-800
                        rounded-xl shadow-sm overflow-hidden">

          {/* CARD HEADER */}
          <div className="px-4 sm:px-6 py-4
                          border-b border-gray-200 dark:border-gray-800">

            <div className="flex items-center gap-3">

              <div className="w-9 h-9 rounded-lg
                              bg-[#80b643]/10 dark:bg-[#80b643]/20
                              flex items-center justify-center">

                <TagIcon className="w-5 h-5 text-[#80b643]" />

              </div>

              <div>
                <h2 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white">
                  Discount Information
                </h2>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Configure the discount used in the sales POS
                </p>
              </div>

            </div>

          </div>

          <form
            onSubmit={handleSubmit}
            autoComplete="off"
          >

            <div className="p-4 sm:p-6">

              {/* FORM FIELDS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                {/* NAME */}
                <div className="md:col-span-2">

                  <label
                    htmlFor="name"
                    className="block text-xs font-medium
                               text-gray-500 dark:text-gray-400 mb-2"
                  >
                    Name
                  </label>

                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={input.name}
                    onChange={handleChange}
                    className={`w-full px-3 py-2.5 rounded-lg
                               bg-white dark:bg-gray-800
                               text-sm text-gray-700 dark:text-white
                               border
                               ${
                                 errors.name
                                   ? 'border-red-400 focus:ring-red-400'
                                   : 'border-gray-200 dark:border-gray-700 focus:border-[#80b643] focus:ring-[#80b643]'
                               }
                               outline-none focus:ring-1
                               transition-all duration-200`}
                  />

                  {errors.name && (
                    <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                      {errors.name}
                    </p>
                  )}

                </div>

                {/* TYPE */}
                <div>

                  <label
                    htmlFor="is_percentage"
                    className="block text-xs font-medium
                               text-gray-500 dark:text-gray-400 mb-2"
                  >
                    Type
                  </label>

                  <select
                    id="is_percentage"
                    name="is_percentage"
                    value={input.is_percentage}
                    onChange={handleChange}
                    className={`w-full px-3 py-2.5 rounded-lg
                               bg-white dark:bg-gray-800
                               text-sm text-gray-700 dark:text-white
                               border
                               ${
                                 errors.is_percentage
                                   ? 'border-red-400 focus:ring-red-400'
                                   : 'border-gray-200 dark:border-gray-700 focus:border-[#80b643] focus:ring-[#80b643]'
                               }
                               outline-none focus:ring-1
                               transition-all duration-200`}
                  >

                    <option
                      value=""
                      className="text-gray-500"
                    >
                      Select Type
                    </option>

                    <option
                      value={1}
                      className="text-gray-800 dark:text-white"
                    >
                      Percentage
                    </option>

                    <option
                      value={0}
                      className="text-gray-800 dark:text-white"
                    >
                      Fixed value
                    </option>

                  </select>

                  {errors.is_percentage && (
                    <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                      {errors.is_percentage}
                    </p>
                  )}

                </div>

                {/* VALUE */}
                <div>

                  <label
                    htmlFor="value"
                    className="block text-xs font-medium
                               text-gray-500 dark:text-gray-400 mb-2"
                  >
                    Value
                  </label>

                  <input
                    type="text"
                    name="value"
                    id="value"
                    value={input.value}
                    onChange={handleChange}
                    className={`w-full px-3 py-2.5 rounded-lg
                               bg-white dark:bg-gray-800
                               text-sm text-gray-700 dark:text-white
                               border
                               ${
                                 errors.value
                                   ? 'border-red-400 focus:ring-red-400'
                                   : 'border-gray-200 dark:border-gray-700 focus:border-[#80b643] focus:ring-[#80b643]'
                               }
                               outline-none focus:ring-1
                               transition-all duration-200`}
                  />

                  {errors.value && (
                    <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                      {errors.value}
                    </p>
                  )}

                </div>

              </div>

              {/* STATUS */}
              <div className="mt-6 p-4 rounded-xl
                              border border-gray-200 dark:border-gray-800
                              bg-gray-50 dark:bg-gray-800/40">

                <div className="flex items-center justify-between gap-4">

                  <div className="min-w-0">

                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Status
                    </label>

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      If activated, this discount will appear among discounts in the sales POS.
                    </p>

                  </div>

                  {/* TOGGLE */}
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">

                    <input
                      type="checkbox"
                      name="status"
                      value={input.status}
                      checked={input.status ? true : false}
                      onChange={handleCheckBox}
                      className="sr-only peer"
                    />

                    <div className="w-11 h-6
                                    bg-gray-300 dark:bg-gray-600
                                    rounded-full
                                    peer-focus:outline-none
                                    peer-focus:ring-2
                                    peer-focus:ring-[#80b643]/30
                                    peer-checked:bg-[#80b643]
                                    after:content-['']
                                    after:absolute
                                    after:top-[2px]
                                    after:left-[2px]
                                    after:bg-white
                                    after:border-gray-300
                                    after:border
                                    after:rounded-full
                                    after:h-5
                                    after:w-5
                                    after:transition-all
                                    peer-checked:after:translate-x-full
                                    peer-checked:after:border-white">
                    </div>

                  </label>

                </div>

              </div>

              {/* DELETE DISCOUNT */}
              <div className="mt-5 p-4 rounded-xl
                              border border-red-200 dark:border-red-900/40
                              bg-red-50 dark:bg-red-950/20">

                <div className="flex flex-col sm:flex-row
                                sm:items-center sm:justify-between gap-4">

                  <div className="flex items-start gap-3">

                    <div className="w-9 h-9 rounded-lg
                                    bg-red-100 dark:bg-red-900/30
                                    flex items-center justify-center
                                    flex-shrink-0">

                      <TrashIcon className="w-5 h-5 text-red-600 dark:text-red-400" />

                    </div>

                    <div>

                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        Delete discount
                      </h3>

                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        You can delete this discount permanently.
                      </p>

                    </div>

                  </div>

                  <button
                    type="button"
                    onClick={(e) => handDelete(e, input.id)}
                    className="inline-flex items-center justify-center gap-2
                               px-4 py-2 rounded-lg
                               border border-red-200 dark:border-red-900/50
                               bg-white dark:bg-gray-900
                               text-red-600 dark:text-red-400
                               text-xs font-medium
                               hover:bg-red-600 hover:text-white
                               hover:border-red-600
                               transition-all duration-200"
                  >

                    <TrashIcon className="w-4 h-4" />

                    Delete

                  </button>

                </div>

              </div>

            </div>

            {/* ACTIONS */}
            <div className="px-4 sm:px-6 py-4
                            border-t border-gray-200 dark:border-gray-800
                            bg-gray-50/50 dark:bg-gray-800/20">

              <div className="flex flex-col-reverse sm:flex-row
                              sm:justify-end gap-3">

                <button
                  type="button"
                  onClick={back}
                  className="inline-flex items-center justify-center gap-2
                             px-5 py-2.5 rounded-xl
                             border border-gray-200 dark:border-gray-700
                             bg-white dark:bg-gray-800
                             text-gray-600 dark:text-gray-300
                             text-sm font-medium
                             hover:bg-gray-50 dark:hover:bg-gray-700
                             transition-all duration-200
                             w-full sm:w-auto"
                >

                  <XIcon className="w-4 h-4" />

                  Cancel

                </button>

                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2
                             px-5 py-2.5 rounded-xl
                             bg-[#80b643] hover:bg-[#719f3a]
                             text-white text-sm font-medium
                             shadow-sm
                             transition-all duration-200
                             w-full sm:w-auto"
                >

                  <SaveIcon className="w-4 h-4" />

                  Save Changes

                </button>

              </div>

            </div>

          </form>

        </div>

      </div>

    </div>
  );
}

export default EditDiscountView;